/**
 * Splash screen — initialises SDK + auth concurrently, then navigates to /home.
 * Respects prefers-reduced-motion: skips animation if set.
 */

import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { initTelegramApp } from "@/tg/init";
import { authenticate } from "@/tg/auth";
import { useAuth } from "@/tg/AuthContext";
import { useI18n } from "@/i18n";
import { BASE, getMe, getSection } from "@/api";
import Logo from "@/components/Logo";

export default function Loading() {
  const navigate = useNavigate();
  const { setAuth } = useAuth();
  const { t, setLang } = useI18n();
  const shouldReduceMotion = useReducedMotion();
  const done = useRef(false);

  useEffect(() => {
    const startMs = Date.now();
    const navigateHome = () => { if (done.current) return; done.current = true; navigate("/home", { replace: true }); };
    const hardCap = setTimeout(navigateHome, 2500);

    async function run() {
      let authResult: Awaited<ReturnType<typeof authenticate>> | undefined;
      try {
        [authResult] = await Promise.all([authenticate(BASE), initTelegramApp()]);
        if (authResult) { setAuth(authResult); if (authResult.lang) setLang(authResult.lang); }
      } catch (err) {
        console.error("[Loading] init error:", err);
      }
      const lang = authResult?.lang ?? "uk";
      void getMe(lang);
      void getSection("fundraisings", lang);
      void getSection("news", lang);
      void getSection("reports", lang);
      const elapsed = Date.now() - startMs;
      if (elapsed < 700) await new Promise((r) => setTimeout(r, 700 - elapsed));
      clearTimeout(hardCap);
      navigateHome();
    }
    run();

    return () => clearTimeout(hardCap);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const logoVariants = shouldReduceMotion
    ? {}
    : {
        initial: { opacity: 0, scale: 0.85 },
        animate: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: "easeOut" } },
      };

  const mottoVariants = shouldReduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 12 },
        animate: { opacity: 1, y: 0, transition: { delay: 0.3, duration: 0.4 } },
      };

  return (
    <div
      className="flex flex-col items-center justify-center h-screen gap-6"
      style={{ background: "var(--brand-graphite)" }}
    >
      <motion.div {...logoVariants} initial="initial" animate="animate">
        <Logo mono size={120} />
      </motion.div>

      <motion.p
        {...mottoVariants}
        initial="initial"
        animate="animate"
        className="text-lg font-semibold tracking-wide"
        style={{ color: "#ffffff" }}
      >
        {t("motto")}
      </motion.p>

      {/* Progress bar */}
      <div
        className="absolute bottom-0 left-0 h-1 rounded-t-full overflow-hidden"
        style={{ width: "100%" }}
      >
        <motion.div
          className="h-full"
          style={{ background: "var(--brand-blue)" }}
          initial={{ scaleX: 0, transformOrigin: "left" }}
          animate={shouldReduceMotion ? { scaleX: 1 } : { scaleX: 1, transition: { duration: 0.8, ease: "easeInOut" } }}
        />
      </div>
    </div>
  );
}
